import { BASE_LOCALE, SupportedLocale } from "@outschool/localization";
import i18n, { InitOptions, i18n as i18nType } from "i18next";
import ChainedBackend from "i18next-chained-backend";
import HttpApi from "i18next-http-backend";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

import { setDayjsLocale } from "../../shared/dayjsLocale";
import {
  ENABLED_FEATURE_FLAGS,
  ENABLE_LOCALE_PSEUDOLOCALIZATION,
  FeatureFlag,
  TRANSLATION_SERVICE_URL,
  isTest
} from "../../shared/Env";
import { pseudo, setLetterRepeatOnPseudo } from "./i18n_pseudolocalization";

const translatingEnabled = ENABLED_FEATURE_FLAGS.includes(
  FeatureFlag.TranslateSiteCopy
);

export const initI18n = (userSelectedLocale: SupportedLocale): i18nType => {
  const i18nOpts: InitOptions = {
    debug: false,
    lng: userSelectedLocale,
    fallbackLng: BASE_LOCALE,
    ns: [
      "categories",
      "client",
      "glossary",
      "shared",
      "ui-components-shared",
      "ui-components-classroom",
      "ui-components-website",
      "ui-legacy-component-library"
    ],
    defaultNS: "client",
    keySeparator: "\\",
    nsSeparator: "\\",
    // This next line is necessary unless we want to polyfill Intl.PluralRules
    // https://www.i18next.com/misc/json-format#i18next-json-v4
    compatibilityJSON: "v3",
    // This function is called when `t` can't find a key, either because
    // the key doesn't exist in JSON, or because there's no JSON file.
    // `key` is the string passed into `t`, including the keyPrefix
    // `defaultValue` is the prop of the same name from `t` options
    parseMissingKeyHandler(key, defaultValue) {
      // This regex removes the prefix from the `key`
      // E.g. client\Button\Hello there! --> Hello there!
      return (defaultValue ?? key).replace(/.*\\([^\\]+)$/, "$1");
    },
    interpolation: {
      // React already escapes, so we don't want to double escape
      escapeValue: false
    },
    react: {
      // This option is not supported by `i18next-parser`, so we turn it off
      // (Open issue: https://github.com/i18next/i18next-parser/issues/264)
      transSupportBasicHtmlNodes: false
    }
  };

  i18n.use(initReactI18next);

  if (ENABLE_LOCALE_PSEUDOLOCALIZATION) {
    // this should never be set in production
    i18n.use(pseudo);
  }

  if (!isTest) {
    i18n.use(ChainedBackend);
    i18nOpts.backend = {
      backends: [],
      backendOptions: []
    };

    const englishBackend = resourcesToBackend((language, ns, callback) => {
      import(`../../../../../../locales/${language}/${ns}.json`)
        .then(({ default: resources }) => {
          callback(null, resources);
        })
        .catch(error => {
          callback(error, null);
        });
    });

    if (TRANSLATION_SERVICE_URL && translatingEnabled) {
      (i18nOpts.backend as any).backends = [HttpApi, englishBackend];
      (i18nOpts.backend as any).backendOptions = [
        {
          loadPath: (languages, namespaces) => {
            const [language] = languages;
            const [namespace] = namespaces;
            const ns = namespace.split("\\").shift();
            return `${TRANSLATION_SERVICE_URL}/${ns}+(${language}).json`;
          }
        }
      ];
    } else {
      (i18nOpts.backend as any).backends = [englishBackend];
    }
  }

  i18n.init(i18nOpts);

  if (translatingEnabled) {
    setDayjsLocale(userSelectedLocale);
  }

  return i18n;
};

export const togglePseudoLocalization = (
  toggleOn: boolean,
  letterRepeat: number
) => {
  if (!ENABLE_LOCALE_PSEUDOLOCALIZATION) {
    console.log("Pseudo-localization is not available");
    return;
  }
  if (toggleOn) {
    console.log(
      "Turning pseudo-localization on with a repeat count of :: " + letterRepeat
    );
    i18n.options.postProcess = ["pseudo"];
    setLetterRepeatOnPseudo(letterRepeat);
  } else {
    console.log("Turning pseudo-localization off");
    i18n.options.postProcess = false;
  }
};
